import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaEye,
  FaFlag,
  FaSearch,
  FaTimesCircle,
  FaTrash,
} from "react-icons/fa";
import Header from "../components/AdminHeader";

const TABS = {
  CONTACT: "contact",
  REPORTS: "reports",
};

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getContactStatusClasses(status) {
  if (status === "Replied") {
    return "border-emerald-400/20 bg-emerald-500/10 text-emerald-100";
  }

  return "border-amber-300/20 bg-amber-500/10 text-amber-100";
}

function getReportStatusClasses(status) {
  if (status === "Resolved") {
    return "border-emerald-400/20 bg-emerald-500/10 text-emerald-100";
  }

  if (status === "Under Review") {
    return "border-cyan-300/20 bg-cyan-500/10 text-cyan-100";
  }

  return "border-fuchsia-400/20 bg-fuchsia-500/10 text-fuchsia-100";
}

export default function StaffDashboard() {
  const [activeTab, setActiveTab] = useState(TABS.CONTACT);

  const [contactRows, setContactRows] = useState([]);
  const [reportRows, setReportRows] = useState([]);

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingRow, setViewingRow] = useState(null);
  const [viewingType, setViewingType] = useState("");

  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    setPageError("");
    setToast("");

    const [
      { data: contactData, error: contactError },
      { data: reportData, error: reportError },
    ] = await Promise.all([
      supabase
        .from("contact_submissions")
        .select(
          "id, full_name, email, subject, message, status, replied_at, staff_reply_message, created_at"
        )
        .order("created_at", { ascending: false }),
      supabase
        .from("staff_reports")
        .select(
          "id, reporter_name, reporter_email, staff_member_name, incident_date, category, details, evidence_links, status, created_at"
        )
        .order("created_at", { ascending: false }),
    ]);

    if (contactError || reportError) {
      console.error("Failed to fetch forms:", { contactError, reportError });
      setContactRows([]);
      setReportRows([]);
      setPageError("Could not load submitted forms.");
      setLoading(false);
      return;
    }

    setContactRows(contactData || []);
    setReportRows(reportData || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const filteredContactRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contactRows;

    return contactRows.filter((row) => {
      return (
        (row.full_name || "").toLowerCase().includes(q) ||
        (row.email || "").toLowerCase().includes(q) ||
        (row.subject || "").toLowerCase().includes(q) ||
        (row.message || "").toLowerCase().includes(q) ||
        (row.status || "").toLowerCase().includes(q)
      );
    });
  }, [contactRows, search]);

  const filteredReportRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reportRows;

    return reportRows.filter((row) => {
      return (
        (row.reporter_name || "").toLowerCase().includes(q) ||
        (row.reporter_email || "").toLowerCase().includes(q) ||
        (row.staff_member_name || "").toLowerCase().includes(q) ||
        (row.category || "").toLowerCase().includes(q) ||
        (row.details || "").toLowerCase().includes(q) ||
        (row.status || "").toLowerCase().includes(q)
      );
    });
  }, [reportRows, search]);

  const awaitingReplyCount = useMemo(
    () =>
      contactRows.filter(
        (row) => (row.status || "Awaiting Reply") === "Awaiting Reply"
      ).length,
    [contactRows]
  );

  const repliedCount = useMemo(
    () => contactRows.filter((row) => row.status === "Replied").length,
    [contactRows]
  );

  const openReportsCount = useMemo(
    () => reportRows.filter((row) => (row.status || "Open") === "Open").length,
    [reportRows]
  );

  const resolvedReportsCount = useMemo(
    () => reportRows.filter((row) => row.status === "Resolved").length,
    [reportRows]
  );

  const openViewModal = (type, row) => {
    setViewingType(type);
    setViewingRow(row);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    if (statusLoadingId || deleteLoadingId) return;
    setViewModalOpen(false);
    setViewingRow(null);
    setViewingType("");
  };

  const handleToggleContactStatus = async (row) => {
    const nextStatus =
      (row.status || "Awaiting Reply") === "Replied"
        ? "Awaiting Reply"
        : "Replied";

    setPageError("");
    setToast("");
    setStatusLoadingId(`contact-${row.id}`);

    const payload =
      nextStatus === "Replied"
        ? {
            status: "Replied",
            replied_at: new Date().toISOString(),
          }
        : {
            status: "Awaiting Reply",
            replied_at: null,
          };

    const { error } = await supabase
      .from("contact_submissions")
      .update(payload)
      .eq("id", row.id);

    if (error) {
      console.error("Failed to update contact status:", error);
      setPageError("Could not update contact form status.");
      setStatusLoadingId(null);
      return;
    }

    setToast(`Contact form marked as ${nextStatus}.`);
    setStatusLoadingId(null);
    await fetchForms();

    if (
      viewModalOpen &&
      viewingType === TABS.CONTACT &&
      viewingRow?.id === row.id
    ) {
      setViewingRow((prev) =>
        prev
          ? {
              ...prev,
              ...payload,
            }
          : prev
      );
    }
  };

  const handleUpdateReportStatus = async (row, nextStatus) => {
    setPageError("");
    setToast("");
    setStatusLoadingId(`report-${row.id}`);

    const { error } = await supabase
      .from("staff_reports")
      .update({
        status: nextStatus,
      })
      .eq("id", row.id);

    if (error) {
      console.error("Failed to update report status:", error);
      setPageError("Could not update staff report status.");
      setStatusLoadingId(null);
      return;
    }

    setToast(`Staff report marked as ${nextStatus}.`);
    setStatusLoadingId(null);
    await fetchForms();

    if (
      viewModalOpen &&
      viewingType === TABS.REPORTS &&
      viewingRow?.id === row.id
    ) {
      setViewingRow((prev) =>
        prev
          ? {
              ...prev,
              status: nextStatus,
            }
          : prev
      );
    }
  };

  const handleDeleteContact = async (row) => {
    const confirmed = window.confirm(
      `Delete contact form from ${row.full_name || row.email || "this user"}?`
    );

    if (!confirmed) return;

    setPageError("");
    setToast("");
    setDeleteLoadingId(`contact-${row.id}`);

    const { error } = await supabase
      .from("contact_submissions")
      .delete()
      .eq("id", row.id);

    if (error) {
      console.error("Failed to delete contact form:", error);
      setPageError("Could not delete contact form.");
      setDeleteLoadingId(null);
      return;
    }

    if (
      viewModalOpen &&
      viewingType === TABS.CONTACT &&
      viewingRow?.id === row.id
    ) {
      closeViewModal();
    }

    setToast("Contact form deleted.");
    setDeleteLoadingId(null);
    await fetchForms();
  };

  const handleDeleteReport = async (row) => {
    const confirmed = window.confirm(
      `Delete staff report about ${row.staff_member_name || "this staff member"}?`
    );

    if (!confirmed) return;

    setPageError("");
    setToast("");
    setDeleteLoadingId(`report-${row.id}`);

    const { error } = await supabase
      .from("staff_reports")
      .delete()
      .eq("id", row.id);

    if (error) {
      console.error("Failed to delete staff report:", error);
      setPageError("Could not delete staff report.");
      setDeleteLoadingId(null);
      return;
    }

    if (
      viewModalOpen &&
      viewingType === TABS.REPORTS &&
      viewingRow?.id === row.id
    ) {
      closeViewModal();
    }

    setToast("Staff report deleted.");
    setDeleteLoadingId(null);
    await fetchForms();
  };

  const visibleRows =
    activeTab === TABS.CONTACT ? filteredContactRows : filteredReportRows;

  return (
    <>
      <Header />

      <div className="min-h-screen bg-[#08111f] text-white">
        <div className="pointer-events-none fixed inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,#4a0454_0%,#3c2f73_35%,#255a86_68%,#12728a_100%)] opacity-90" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_24%)]" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative mx-auto max-w-[1800px] px-4 py-5 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
          <section className="mb-8 overflow-hidden rounded-[30px] border border-white/10 bg-[linear-gradient(135deg,rgba(86,24,113,0.45),rgba(39,78,125,0.45),rgba(18,111,135,0.40))] shadow-[0_20px_80px_rgba(0,0,0,0.32)] backdrop-blur-xl">
            <div className="grid gap-8 px-5 py-6 sm:px-8 sm:py-8 lg:grid-cols-[1.1fr_0.9fr] lg:px-10 lg:py-10">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.24em] text-white/80">
                  <FaEnvelope />
                  <span>Forms Dashboard</span>
                </div>

                <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">
                  Benton Submitted Forms
                </h1>

                <p className="mt-4 max-w-3xl text-sm leading-7 text-white/80 sm:text-base lg:text-lg">
                  View all submitted contact forms and staff reports in one admin
                  dashboard.
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setActiveTab(TABS.CONTACT)}
                    className={[
                      "inline-flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition",
                      activeTab === TABS.CONTACT
                        ? "border-cyan-200/40 bg-cyan-300 text-slate-950 shadow-[0_10px_30px_rgba(103,232,249,0.25)]"
                        : "border-white/10 bg-white/[0.06] text-white hover:bg-white/[0.10]",
                    ].join(" ")}
                  >
                    <FaEnvelope />
                    <span>Contact Forms</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab(TABS.REPORTS)}
                    className={[
                      "inline-flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition",
                      activeTab === TABS.REPORTS
                        ? "border-cyan-200/40 bg-cyan-300 text-slate-950 shadow-[0_10px_30px_rgba(103,232,249,0.25)]"
                        : "border-white/10 bg-white/[0.06] text-white hover:bg-white/[0.10]",
                    ].join(" ")}
                  >
                    <FaFlag />
                    <span>Staff Reports</span>
                  </button>
                </div>
              </div>

              <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 backdrop-blur-xl sm:p-5">
                <div className="mb-4 text-sm font-medium text-white/90">
                  Dashboard summary
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Contact forms
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {contactRows.length}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Staff reports
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {reportRows.length}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Awaiting reply
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {awaitingReplyCount}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Replied
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {repliedCount}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Open reports
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {openReportsCount}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                      Resolved reports
                    </div>
                    <div className="mt-2 text-xl font-semibold text-white">
                      {resolvedReportsCount}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full max-w-[420px]">
                <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={
                    activeTab === TABS.CONTACT
                      ? "Search name, email, subject, message or status"
                      : "Search reporter, staff member, category, details or status"
                  }
                  className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                />
              </div>

              <div className="text-sm text-white/60">
                Showing {visibleRows.length} result
                {visibleRows.length === 1 ? "" : "s"}
              </div>
            </div>

            {pageError ? (
              <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
                <FaExclamationTriangle className="mt-0.5 shrink-0" />
                <span>{pageError}</span>
              </div>
            ) : null}

            {toast ? (
              <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
                <FaCheckCircle className="mt-0.5 shrink-0" />
                <span>{toast}</span>
              </div>
            ) : null}

            {loading ? (
              <div className="grid min-h-[240px] place-items-center rounded-[24px] border border-white/10 bg-white/[0.04] text-sm text-white/70">
                Loading submitted forms...
              </div>
            ) : activeTab === TABS.CONTACT ? (
              <div className="overflow-x-auto">
                <div className="min-w-[1550px]">
                  <div className="mb-3 grid grid-cols-[1.3fr_1.4fr_1.4fr_1.1fr_1.2fr_1.2fr_1.3fr] gap-4 px-4 text-xs uppercase tracking-[0.24em] text-white/45">
                    <div>Name</div>
                    <div>Email</div>
                    <div>Subject</div>
                    <div>Status</div>
                    <div>Submitted</div>
                    <div>Replied At</div>
                    <div>Actions</div>
                  </div>

                  <div className="space-y-3">
                    {filteredContactRows.length === 0 ? (
                      <div className="rounded-[24px] border border-white/10 bg-white/[0.04] px-5 py-8 text-center text-sm text-white/70">
                        No contact forms found.
                      </div>
                    ) : (
                      filteredContactRows.map((row) => (
                        <div
                          key={row.id}
                          className="grid grid-cols-[1.3fr_1.4fr_1.4fr_1.1fr_1.2fr_1.2fr_1.3fr] items-center gap-4 rounded-[24px] border border-white/10 bg-white/[0.04] px-4 py-4"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">
                              {row.full_name}
                            </div>
                          </div>

                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">
                              {row.email}
                            </div>
                          </div>

                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">
                              {row.subject}
                            </div>
                          </div>

                          <div>
                            <span
                              className={[
                                "inline-flex rounded-full border px-3 py-1 text-xs font-medium",
                                getContactStatusClasses(
                                  row.status || "Awaiting Reply"
                                ),
                              ].join(" ")}
                            >
                              {row.status || "Awaiting Reply"}
                            </span>
                          </div>

                          <div className="text-sm text-white/80">
                            {formatDateTime(row.created_at)}
                          </div>

                          <div className="text-sm text-white/80">
                            {formatDateTime(row.replied_at)}
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openViewModal(TABS.CONTACT, row)}
                              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white transition hover:bg-white/[0.10]"
                            >
                              <FaEye />
                              <span>View</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleToggleContactStatus(row)}
                              disabled={statusLoadingId === `contact-${row.id}`}
                              className="inline-flex items-center gap-2 rounded-xl border border-cyan-300/20 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100 transition hover:bg-cyan-500/20 disabled:opacity-60"
                            >
                              {row.status === "Replied" ? (
                                <>
                                  <FaTimesCircle />
                                  <span>Mark Awaiting</span>
                                </>
                              ) : (
                                <>
                                  <FaCheckCircle />
                                  <span>Mark Replied</span>
                                </>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteContact(row)}
                              disabled={deleteLoadingId === `contact-${row.id}`}
                              className="inline-flex items-center gap-2 rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-sm text-red-100 transition hover:bg-red-500/20 disabled:opacity-60"
                            >
                              <FaTrash />
                              <span>
                                {deleteLoadingId === `contact-${row.id}`
                                  ? "Deleting..."
                                  : "Delete"}
                              </span>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="min-w-[1600px]">
                  <div className="mb-3 grid grid-cols-[1.2fr_1.3fr_1.2fr_1fr_1fr_1.2fr_1.5fr] gap-4 px-4 text-xs uppercase tracking-[0.24em] text-white/45">
                    <div>Reporter</div>
                    <div>Email</div>
                    <div>Staff Member</div>
                    <div>Category</div>
                    <div>Status</div>
                    <div>Submitted</div>
                    <div>Actions</div>
                  </div>

                  <div className="space-y-3">
                    {filteredReportRows.length === 0 ? (
                      <div className="rounded-[24px] border border-white/10 bg-white/[0.04] px-5 py-8 text-center text-sm text-white/70">
                        No staff reports found.
                      </div>
                    ) : (
                      filteredReportRows.map((row) => (
                        <div
                          key={row.id}
                          className="grid grid-cols-[1.2fr_1.3fr_1.2fr_1fr_1fr_1.2fr_1.5fr] items-center gap-4 rounded-[24px] border border-white/10 bg-white/[0.04] px-4 py-4"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">
                              {row.reporter_name}
                            </div>
                          </div>

                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">
                              {row.reporter_email}
                            </div>
                          </div>

                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-white">
                              {row.staff_member_name}
                            </div>
                          </div>

                          <div className="text-sm font-medium text-white">
                            {row.category || "—"}
                          </div>

                          <div>
                            <span
                              className={[
                                "inline-flex rounded-full border px-3 py-1 text-xs font-medium",
                                getReportStatusClasses(row.status || "Open"),
                              ].join(" ")}
                            >
                              {row.status || "Open"}
                            </span>
                          </div>

                          <div className="text-sm text-white/80">
                            {formatDateTime(row.created_at)}
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openViewModal(TABS.REPORTS, row)}
                              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white transition hover:bg-white/[0.10]"
                            >
                              <FaEye />
                              <span>View</span>
                            </button>

                            <select
                              value={row.status || "Open"}
                              onChange={(e) =>
                                handleUpdateReportStatus(row, e.target.value)
                              }
                              disabled={statusLoadingId === `report-${row.id}`}
                              className="h-10 rounded-xl border border-white/10 bg-white/[0.08] px-3 text-sm text-white outline-none transition focus:border-cyan-300/40 disabled:opacity-60"
                            >
                              <option
                                value="Open"
                                className="bg-[#142138] text-white"
                              >
                                Open
                              </option>
                              <option
                                value="Under Review"
                                className="bg-[#142138] text-white"
                              >
                                Under Review
                              </option>
                              <option
                                value="Resolved"
                                className="bg-[#142138] text-white"
                              >
                                Resolved
                              </option>
                            </select>

                            <button
                              type="button"
                              onClick={() => handleDeleteReport(row)}
                              disabled={deleteLoadingId === `report-${row.id}`}
                              className="inline-flex items-center gap-2 rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-sm text-red-100 transition hover:bg-red-500/20 disabled:opacity-60"
                            >
                              <FaTrash />
                              <span>
                                {deleteLoadingId === `report-${row.id}`
                                  ? "Deleting..."
                                  : "Delete"}
                              </span>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>

        {viewModalOpen && viewingRow ? (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-[860px] rounded-[28px] border border-white/10 bg-[#101a2b] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                    {viewingType === TABS.CONTACT
                      ? "Contact submission"
                      : "Staff report"}
                  </div>
                  <h3 className="mt-1 text-2xl font-semibold text-white">
                    {viewingType === TABS.CONTACT
                      ? viewingRow.subject || "Contact form"
                      : viewingRow.staff_member_name || "Staff report"}
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={closeViewModal}
                  className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-2 text-sm font-medium text-white transition hover:bg-white/[0.10]"
                >
                  Close
                </button>
              </div>

              {viewingType === TABS.CONTACT ? (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Full Name
                      </div>
                      <div className="mt-2 text-sm font-medium text-white">
                        {viewingRow.full_name || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Email
                      </div>
                      <div className="mt-2 text-sm font-medium text-white break-all">
                        {viewingRow.email || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Status
                      </div>
                      <div className="mt-2">
                        <span
                          className={[
                            "inline-flex rounded-full border px-3 py-1 text-xs font-medium",
                            getContactStatusClasses(
                              viewingRow.status || "Awaiting Reply"
                            ),
                          ].join(" ")}
                        >
                          {viewingRow.status || "Awaiting Reply"}
                        </span>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Submitted
                      </div>
                      <div className="mt-2 text-sm font-medium text-white">
                        {formatDateTime(viewingRow.created_at)}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Replied At
                      </div>
                      <div className="mt-2 text-sm font-medium text-white">
                        {formatDateTime(viewingRow.replied_at)}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Subject
                      </div>
                      <div className="mt-2 text-sm font-medium text-white">
                        {viewingRow.subject || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                      Message
                    </div>
                    <div className="mt-3 whitespace-pre-wrap text-sm leading-7 text-white/85">
                      {viewingRow.message || "—"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                      Staff Reply Message
                    </div>
                    <div className="mt-3 whitespace-pre-wrap text-sm leading-7 text-white/85">
                      {viewingRow.staff_reply_message ||
                        "No staff reply message saved."}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => handleDeleteContact(viewingRow)}
                      disabled={deleteLoadingId === `contact-${viewingRow.id}`}
                      className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-100 transition hover:bg-red-500/20 disabled:opacity-60"
                    >
                      {deleteLoadingId === `contact-${viewingRow.id}`
                        ? "Deleting..."
                        : "Delete Form"}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleToggleContactStatus(viewingRow)}
                      disabled={statusLoadingId === `contact-${viewingRow.id}`}
                      className="rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-4 py-3 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
                    >
                      {statusLoadingId === `contact-${viewingRow.id}`
                        ? "Updating..."
                        : viewingRow.status === "Replied"
                          ? "Mark as Awaiting Reply"
                          : "Mark as Replied"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Reporter Name
                      </div>
                      <div className="mt-2 text-sm font-medium text-white">
                        {viewingRow.reporter_name || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Reporter Email
                      </div>
                      <div className="mt-2 text-sm font-medium text-white break-all">
                        {viewingRow.reporter_email || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Staff Member
                      </div>
                      <div className="mt-2 text-sm font-medium text-white">
                        {viewingRow.staff_member_name || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Category
                      </div>
                      <div className="mt-2 text-sm font-medium text-white">
                        {viewingRow.category || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Status
                      </div>
                      <div className="mt-2">
                        <span
                          className={[
                            "inline-flex rounded-full border px-3 py-1 text-xs font-medium",
                            getReportStatusClasses(viewingRow.status || "Open"),
                          ].join(" ")}
                        >
                          {viewingRow.status || "Open"}
                        </span>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Incident Date
                      </div>
                      <div className="mt-2 text-sm font-medium text-white">
                        {viewingRow.incident_date || "—"}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:col-span-2">
                      <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                        Submitted
                      </div>
                      <div className="mt-2 text-sm font-medium text-white">
                        {formatDateTime(viewingRow.created_at)}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                      Details
                    </div>
                    <div className="mt-3 whitespace-pre-wrap text-sm leading-7 text-white/85">
                      {viewingRow.details || "—"}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                      Evidence Links
                    </div>
                    <div className="mt-3 whitespace-pre-wrap break-all text-sm leading-7 text-white/85">
                      {viewingRow.evidence_links || "No evidence links provided."}
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => handleDeleteReport(viewingRow)}
                      disabled={deleteLoadingId === `report-${viewingRow.id}`}
                      className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-100 transition hover:bg-red-500/20 disabled:opacity-60"
                    >
                      {deleteLoadingId === `report-${viewingRow.id}`
                        ? "Deleting..."
                        : "Delete Report"}
                    </button>

                    <select
                      value={viewingRow.status || "Open"}
                      onChange={(e) =>
                        handleUpdateReportStatus(viewingRow, e.target.value)
                      }
                      disabled={statusLoadingId === `report-${viewingRow.id}`}
                      className="h-12 rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition focus:border-cyan-300/40 disabled:opacity-60"
                    >
                      <option value="Open" className="bg-[#142138] text-white">
                        Open
                      </option>
                      <option
                        value="Under Review"
                        className="bg-[#142138] text-white"
                      >
                        Under Review
                      </option>
                      <option
                        value="Resolved"
                        className="bg-[#142138] text-white"
                      >
                        Resolved
                      </option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}